import { t } from "i18next";
import { useEffect, useState } from "react";
import { RequestTypeOf } from "../generated/routes";
import { AccountWithoutPasswordAndCompany } from "../generated/types";
import { apiFetchRoute } from "./apiFetchRoute";
import { toUpdateAccount } from "./user";

// Fonction pour récupérer les données du profil
export const fetchAccountStorage = async () => {
  const response = await apiFetchRoute("getAccountProfile", undefined);

  if (!response.ok) {
    throw new Error("Erreur lors de la récupération du compte");
  }

  const data = await response.json();
  const accountStorage = {
    account: {
      id: data.account.id,
      type: data.account.type,
      language: data.account.language,
      id_company: data.account.id_company,
      email: data.account.email,
      first_name: data.account.first_name,
      last_name: data.account.last_name,
      phone_mobile: data.account.phone_mobile,
      phone_fixe: data.account.phone_fixe,
      creation_date: data.account.creation_date,
      avatar: data.account.avatar,
      is_activated: data.account.is_activated,
    },
    company: {
      id: data.company.id,
      id_x3: data.company.id_x3,
      creation_date: data.company.creation_date,
      company_name: data.company.company_name,
      siret: data.company.siret,
      address: data.company.address,
      address_complementary: data.company.address_complementary || "",
      zip_code: data.company.zip_code,
      city: data.company.city,
      country: data.company.country,
      phone_mobile: data.company.phone_mobile,
      phone_fixe: data.company.phone_fixe,
      email: data.company.email || "",
    },
  } as AccountWithoutPasswordAndCompany;

  // Sauvegarder les données utilisateur dans le localStorage
  localStorage.setItem(
    "accountStorage",
    JSON.stringify({
      first_name: accountStorage.account.first_name,
      last_name: accountStorage.account.last_name,
      company_name: accountStorage.company.company_name,
      email: accountStorage.account.email,
      type: accountStorage.account.type,
      avatar: accountStorage.account.avatar,
    })
  );

  return accountStorage;
};

// Hook pour gérer l'état du profil
export const useProfile = () => {
  const [profileData, setProfileData] =
    useState<AccountWithoutPasswordAndCompany | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const accountStorage = await fetchAccountStorage();
        setProfileData(accountStorage);
      } catch (err) {
        console.error("Erreur lors de la récupération des données:", err);
        setError(t("profile-load-error"));
      } finally {
        setIsLoading(false);
      }
    };

    loadProfile();
  }, []);

  return { profileData, isLoading, error, setProfileData };
};

// Fonction pour mettre à jour le profil
export const updateProfile = async (
  profileData: Partial<AccountWithoutPasswordAndCompany>
) => {
  try {
    // Vérifier que les données nécessaires sont présentes
    if (!profileData.account || !profileData.company) {
      return {
        success: false,
        error: "Données de profil incomplètes",
      };
    }

    // Mettre à jour le compte
    const response = await apiFetchRoute(
      "putAccounts",
      toUpdateAccount(profileData.account)
    );

    if (!response.ok) {
      return {
        success: false,
        error: t("profile-update-error"),
      };
    }

    // Mettre à jour l'entreprise
    type UpdateCompanyRequest = RequestTypeOf<"putCompanies">;

    const bodyCompany: UpdateCompanyRequest = profileData.company;
    const companyResponse = await apiFetchRoute("putCompanies", bodyCompany);

    // Vérifier les réponses
    if (!response.ok || !companyResponse.ok) {
      return {
        success: false,
        error: t("profile-update-error"),
      };
    }

    // Mettre à jour le localStorage avec les données envoyées
    // puisque le serveur ne renvoie pas de données
    localStorage.setItem(
      "accountStorage",
      JSON.stringify({
        first_name: profileData.account.first_name,
        last_name: profileData.account.last_name,
        company_name: profileData.company.company_name,
        email: profileData.account.email,
        type: profileData.account.type,
        avatar: profileData.account.avatar,
      })
    );

    // Retourner les données envoyées comme données mises à jour
    return {
      success: true,
      data: {
        account: profileData.account,
        company: profileData.company,
      },
    };
  } catch (error) {
    console.error("Erreur lors de la mise à jour du profil:", error);
    return {
      success: false,
      error: t("profile-update-error"),
    };
  }
};

// Fonction pour mettre à jour l'avatar.
// Historiquement, ça tapait `PUT /account/avatar` — une route qui N'EXISTE PAS côté DATA (404,
// avatar jamais persisté). On passe désormais par `putAccounts`, le SEUL chemin qui persiste
// réellement l'avatar (USERS.UpdateAccount inclut la colonne avatar). `putAccounts` écrit tous
// les champs du compte : on renvoie donc le compte courant complet + le nouvel avatar, pour ne
// pas écraser les autres champs. `avatarId` = un id du catalogue (voir avatarCatalog.ts).
export const updateAvatar = async (
  avatarId: string,
  account: AccountWithoutPasswordAndCompany["account"]
) => {
  const response = await apiFetchRoute(
    "putAccounts",
    toUpdateAccount({ ...account, avatar: avatarId })
  );

  if (!response.ok) {
    throw new Error("Erreur lors de la mise à jour de l'avatar");
  }

  // Mettre à jour le localStorage avec le nouvel avatar
  const accountStorage = JSON.parse(
    localStorage.getItem("accountStorage") || "{}"
  );
  localStorage.setItem(
    "accountStorage",
    JSON.stringify({
      ...accountStorage,
      avatar: avatarId,
    })
  );

  return response.json();
};

// Fonction pour mettre à jour le mot de passe
export const updatePassword = async (
  currentPassword: string,
  newPassword: string
) => {
  type PutAccountsUpdateoldpasswordRequest =
    RequestTypeOf<"putAccountsUpdateoldpassword">;

  const body: PutAccountsUpdateoldpasswordRequest = {
    old_password: currentPassword,
    new_password: newPassword,
  };
  const response = await apiFetchRoute("putAccountsUpdateoldpassword", body);

  if (response.status === 400) {
    throw new Error("current_password_incorrect");
  }

  if (!response.ok) {
    throw new Error("update_error");
  }

  return response.json();
};

// Fonction pour supprimer le compte
export const deleteAccount = async (id: number) => {
  const response = await apiFetchRoute("deleteAccountsId", undefined, {
    params: { id },
  });

  if (!response.ok) {
    throw new Error("Erreur lors de la suppression du compte");
  }
};
